import { AxiosResponse } from 'axios';
import {
  AvailablePlugin,
  CANONICAL_DOCUMENT_EMAIL_ROLES,
  TenantConfiguration,
  TenantConfigurationListResponse,
  TenantConfigurationResponse,
  TenantStatusResponse,
  SingleTenantStatusResponse,
  VoiceDatabaseResponse,
} from 'types/Tenant';
import {
  SyncApplyRequest,
  SyncApplyResponse,
  SyncConflict,
  SyncPreviewRequest,
  SyncPreviewResponse,
} from 'types/ConfigSync';
import { API_BASE_URL } from 'config/api';
import { DOCUMENT_TYPES, getOrderDeliveryResolutionFields } from 'components/tenant/outbound';
import { coalesceWorkDescriptionMapping } from 'components/tenant/configuration/workDescriptionMappingCoalesce';
import { createApiClient, extractApiError, isAbortError } from './apiClient';
import { toConfigSaveError } from './tenantConfigSaveError';

const apiClient = createApiClient('TENANT', { logging: false });

export default class TenantService {
  static async getTenantConfigurations(signal?: AbortSignal): Promise<TenantConfigurationListResponse> {
    try {
      console.log('⚙️ [TENANT] Fetching tenant configuration list:', {
        url: `${API_BASE_URL}/config/tenant/`,
        method: 'GET'
      });

      const response: AxiosResponse<TenantConfigurationListResponse> = await apiClient.get('/config/tenant/', { signal });

      console.log('✅ [TENANT] Tenant configuration list response:', {
        status: response.status,
        statusText: response.statusText,
        count: response.data.count
      });

      return response.data;
    } catch (error: any) {
      if (isAbortError(error)) throw error;
      console.error('❌ [TENANT] Error fetching tenant configuration list:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to fetch tenant configuration list');
    }
  }

  static async getSingleTenantStatus(tenantId: string, signal?: AbortSignal): Promise<SingleTenantStatusResponse> {
    try {
      console.log('🏢 [TENANT] Fetching single tenant status:', {
        tenantId,
        url: `${API_BASE_URL}/config/tenant-status/${tenantId}`,
        method: 'GET'
      });

      const response: AxiosResponse<SingleTenantStatusResponse> = await apiClient.get(`/config/tenant-status/${tenantId}`, { signal });

      return response.data;
    } catch (error: any) {
      if (isAbortError(error)) throw error;
      // Gracefully handle 404 Not Found for tenants without status yet
      if (error.response?.status === 404) {
        console.warn(`⚠️ [TENANT] Status not found for tenant ${tenantId}. Treating as not found.`);
        return {
          message: 'Tenant status not found',
          tenant_id: tenantId,
          // status is absent in this case; set found=false and leave status undefined as any
          // but conform to type by casting undefined to any
          status: undefined as any,
          found: false
        };
      }

      console.error('❌ [TENANT] Error fetching tenant status:', {
        tenantId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to fetch tenant status');
    }
  }
  static async getTenantStatuses(): Promise<TenantStatusResponse> {
    try {
      console.log('🏢 [TENANT] Fetching tenant statuses:', {
        url: `${API_BASE_URL}/config/tenant-status/`,
        method: 'GET'
      });
      
      const response: AxiosResponse<TenantStatusResponse> = await apiClient.get('/config/tenant-status/');
      
      console.log('✅ [TENANT] Tenant statuses response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_count: response.data.tenant_statuses?.length || 0,
          tenants: response.data.tenant_statuses?.map(t => ({
            tenant_id: t.tenant_id,
            is_active: t.is_active
          }))
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [TENANT] Error fetching tenant statuses:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to fetch tenant statuses');
    }
  }

  static async getTenantConfiguration(tenantId: string): Promise<TenantConfigurationResponse> {
    try {
      console.log('⚙️ [TENANT] Fetching tenant configuration:', {
        tenantId,
        url: `${API_BASE_URL}/config/tenant/${tenantId}`,
        method: 'GET'
      });
      
      const response: AxiosResponse<TenantConfigurationResponse> = await apiClient.get(`/config/tenant/${tenantId}`);
      
      console.log('✅ [TENANT] Tenant configuration response:', {
        status: response.status,
        statusText: response.statusText,
        data: {
          tenant_id: response.data.tenant_id,
          status: response.data.status,
          config_sections: Object.keys(response.data.config)
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [TENANT] Error fetching tenant configuration:', {
        tenantId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to fetch tenant configuration');
    }
  }

  /**
   * Partially update tenant configuration - only updates the specified fields
   * This is safer and more efficient than updating the entire configuration
   * @param tenantId - Tenant identifier
   * @param partialConfig - Only the configuration fields to update
   * @returns Promise with the response
   */
  static async updateTenantConfigurationPartial(
    tenantId: string,
    partialConfig: Partial<TenantConfiguration>
  ): Promise<TenantConfigurationResponse> {
    try {
      // zoom_factor is deprecated; never send it back to server
      if (partialConfig.pdf_processing && (partialConfig.pdf_processing as any).zoom_factor !== undefined) {
        const { zoom_factor, ...rest } = partialConfig.pdf_processing as any;
        partialConfig = { ...partialConfig, pdf_processing: rest };
      }

      console.log('⚙️ [TENANT] Partially updating tenant configuration:', {
        tenantId,
        url: `${API_BASE_URL}/config/tenant-config/${tenantId}`,
        method: 'POST',
        updated_sections: Object.keys(partialConfig)
      });

      // Try the partial update endpoint first
      let response: AxiosResponse<TenantConfigurationResponse>;
      try {
        response = await apiClient.post(
          `/config/tenant-config/${tenantId}`,
          {
            config_data: partialConfig,
          }
        );
      } catch (partialError: any) {
        // If partial endpoint doesn't exist (405), fall back to full update endpoint
        // but still only send partial data - backend may handle it
        if (partialError.response?.status === 405 || partialError.response?.status === 404) {
          console.log('⚠️ [TENANT] Partial update endpoint not available, trying full update endpoint with partial data');
          response = await apiClient.post(
            `/config/tenant/${tenantId}`,
            {
              config_data: partialConfig,
            }
          );
        } else {
          throw partialError;
        }
      }

      console.log('✅ [TENANT] Partial configuration update response:', {
        status: response.status,
        statusText: response.statusText,
        message: response.data.message
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ [TENANT] Error partially updating tenant configuration:', {
        tenantId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });

      throw extractApiError(error, 'Failed to partially update tenant configuration');
    }
  }

  /**
   * Full-document save. Pass `baseHash` (the `config_hash` the config was loaded
   * with, or the one the previous save returned) so the server refuses a save
   * made from a stale copy with 409. The response carries the new `config_hash`.
   * Failures the server explains (400/403/409/422) throw a `ConfigSaveError`.
   */
  static async updateTenantConfiguration(
    tenantId: string,
    configuration: TenantConfiguration,
    options: { baseHash?: string } = {},
  ): Promise<TenantConfigurationResponse> {
    try {
      // zoom_factor is deprecated; never send it back to server
      if ((configuration.pdf_processing as any)?.zoom_factor !== undefined) {
        const { zoom_factor, ...rest } = configuration.pdf_processing as any;
        configuration = { ...configuration, pdf_processing: rest } as TenantConfiguration;
      }

      // Validate customer_catalog.role_emails as an atomic optional block.
      // Backend rejects partial blocks; we fail fast with a clear form-level error.
      const roleEmails = configuration.data_ingestion?.customer_catalog?.role_emails;
      if (roleEmails !== undefined) {
        const missing: string[] = [];
        if (!roleEmails.sheet_name?.trim()) missing.push('data_ingestion.customer_catalog.role_emails.sheet_name');
        if (!roleEmails.customer_number_column?.trim()) missing.push('data_ingestion.customer_catalog.role_emails.customer_number_column');
        if (!roleEmails.email_column?.trim()) missing.push('data_ingestion.customer_catalog.role_emails.email_column');
        if (!roleEmails.role_column?.trim()) missing.push('data_ingestion.customer_catalog.role_emails.role_column');
        if (missing.length > 0) {
          throw new Error(`Missing required fields: ${missing.join(', ')}`);
        }

        if (roleEmails.role_mapping) {
          const invalidValues = Object.entries(roleEmails.role_mapping)
            .filter(([, v]) => !CANONICAL_DOCUMENT_EMAIL_ROLES.includes(v as any))
            .map(([k, v]) => `${k}=${String(v)}`);
          if (invalidValues.length > 0) {
            throw new Error(
              `Missing required fields: data_ingestion.customer_catalog.role_emails.role_mapping has invalid values (${invalidValues.join(
                ', ',
              )})`,
            );
          }
        }
      }

      console.log('⚙️ [TENANT] Updating tenant configuration:', {
        tenantId,
        url: `${API_BASE_URL}/config/tenant/${tenantId}`,
        method: 'POST',
        config_sections: Object.keys(configuration)
      });
      
      // Filter connectors - only include properly configured ones
      // A connector is considered configured if it has all required fields filled
      // Note: microsoft_graph_api is always required by the server, so we always include it
      const filteredConnectors: any = {};
      if (configuration.connectors.microsoft_graph_api) {
        // Always include microsoft_graph_api as the server requires it to be present
        filteredConnectors.microsoft_graph_api = configuration.connectors.microsoft_graph_api;
      } else {
        // If not present, include empty structure to satisfy server validation
        filteredConnectors.microsoft_graph_api = {
          client_id: '',
          client_secret: '',
          account: '',
          tenant_id: ''
        };
      }
      if (configuration.connectors.business_central) {
        // Persist whatever is filled; partial configs are allowed so users can
        // configure incrementally. The UI surfaces a warning (see
        // getPartialConnectorWarnings) when not all fields are set, since the
        // connector only becomes active once it is complete.
        filteredConnectors.business_central = configuration.connectors.business_central;
      }
      if (configuration.connectors.sap) {
        filteredConnectors.sap = configuration.connectors.sap;
      }
      if (configuration.connectors.exchange_on_prem) {
        filteredConnectors.exchange_on_prem = configuration.connectors.exchange_on_prem;
      }
      if (configuration.connectors.gmail_api) {
        const gmail = configuration.connectors.gmail_api;
        
        // New approach: JSON content (recommended)
        const oauth2CredentialsJson = (gmail.oauth2_credentials_json || '').trim();
        const serviceAccountJson = (gmail.service_account_json || '').trim();
        
        // Legacy approach: File paths (deprecated)
        const serviceAccountPath = (gmail.service_account_path || '').trim();
        const credentialsPath = (gmail.oauth2_credentials_path || '').trim();
        const account = (gmail.account || '').trim();
        
        // OAuth2 flow: requires oauth2_credentials_json (new) OR oauth2_credentials_path (legacy, deprecated)
        const hasOAuth2Auth = oauth2CredentialsJson !== '' || credentialsPath !== '';
        
        // Service account flow: requires service_account_json (new) OR service_account_path (legacy) + account
        const hasServiceAccountAuth = serviceAccountJson !== '' || serviceAccountPath !== '';
        const hasServiceAccountConfig = hasServiceAccountAuth && account !== '';
        
        // Connector is configured if either OAuth2 or service account is properly set up
        const hasAllRequiredFields = hasOAuth2Auth || hasServiceAccountConfig;
        
        // Check if connector has partial data
        const hasPartialData = oauth2CredentialsJson !== '' || serviceAccountJson !== '' || 
                              account !== '' || serviceAccountPath !== '' || 
                              credentialsPath !== '';
        
        // Only include if ALL required fields are present
        if (hasAllRequiredFields) {
          filteredConnectors.gmail_api = gmail;
        } else if (hasPartialData) {
          // Incomplete connector - just exclude it from payload, don't block the save
          const missingFields: string[] = [];
          if (!hasOAuth2Auth && !hasServiceAccountConfig) {
            if (oauth2CredentialsJson === '' && credentialsPath === '' && serviceAccountJson === '' && serviceAccountPath === '') {
              missingFields.push('Either OAuth2 credentials (oauth2_credentials_json or oauth2_credentials_path) or Service Account credentials (service_account_json or service_account_path)');
            }
            if ((serviceAccountJson !== '' || serviceAccountPath !== '') && account === '') {
              missingFields.push('Account email is required when using Service Account authentication');
            }
          }
          
          console.warn('Gmail API connector is incomplete, excluding from payload:', missingFields.join(', '));
        }
        // If any required field is missing or empty, the connector is NOT included in the payload
      }
      
      // Filter document_integrations - only include properly configured ones (without 'config' field)
      const filteredDocumentIntegrations: any = {};

      DOCUMENT_TYPES.forEach(docType => {
        const integration = configuration.document_integrations?.[docType as keyof typeof configuration.document_integrations];
        if (integration && integration.integration_type?.trim()) {
          const filteredIntegration: any = {
            integration_type: integration.integration_type,
            enabled: integration.enabled ?? false,
          };
          // Only include validation_method for order type
          if (docType === 'order' && integration.validation_method) {
            filteredIntegration.validation_method = integration.validation_method;
          }
          // Order-only flags
          if (docType === 'order') {
            filteredIntegration.requires_confirmation_as_work_description =
              integration.requires_confirmation_as_work_description ?? false;
            Object.assign(filteredIntegration, getOrderDeliveryResolutionFields(integration));
          }
          // Include idoc config for SAP IDoc order integrations
          if (docType === 'order' && integration.idoc) {
            filteredIntegration.idoc = integration.idoc;
          }
          // product_description-specific fields
          if (docType === 'product_description') {
            if (integration.output_mode !== undefined) {
              filteredIntegration.output_mode = integration.output_mode;
            }
            if (integration.enrichment_enabled !== undefined) {
              filteredIntegration.enrichment_enabled = integration.enrichment_enabled;
            }
            if (integration.web_search_fallback_enabled !== undefined) {
              filteredIntegration.web_search_fallback_enabled = integration.web_search_fallback_enabled;
            }
            if (integration.refinement_batch_size !== undefined) {
              filteredIntegration.refinement_batch_size = integration.refinement_batch_size;
            }
            if (integration.swap_eszett !== undefined) {
              filteredIntegration.swap_eszett = integration.swap_eszett;
            }
            if (integration.url_template !== undefined && integration.url_template !== '') {
              filteredIntegration.url_template = integration.url_template;
            }
            if (integration.url_slug_stopword_languages !== undefined) {
              filteredIntegration.url_slug_stopword_languages = integration.url_slug_stopword_languages;
            }
            if (integration.attribute_lookups !== undefined) {
              filteredIntegration.attribute_lookups = integration.attribute_lookups;
            }
          }
          filteredDocumentIntegrations[docType] = filteredIntegration;
        }
      });

      // Scripts: server only uses type, enabled, script for customer/product rules. quote_matching is sent as-is.
      let scriptsPayload: Record<string, any> | undefined;
      if (configuration.scripts) {
        scriptsPayload = {};
        if (configuration.scripts.customer_filter) {
          scriptsPayload.customer_filter = {
            type: configuration.scripts.customer_filter.type || 'CEL',
            enabled: configuration.scripts.customer_filter.enabled ?? true,
            script: configuration.scripts.customer_filter.script ?? '',
          };
        }
        if (configuration.scripts.customer_disambiguation) {
          scriptsPayload.customer_disambiguation = {
            type: configuration.scripts.customer_disambiguation.type || 'CEL',
            enabled: configuration.scripts.customer_disambiguation.enabled ?? true,
            script: configuration.scripts.customer_disambiguation.script ?? '',
          };
        }
        if (configuration.scripts.product_filter) {
          scriptsPayload.product_filter = {
            type: configuration.scripts.product_filter.type || 'CEL',
            enabled: configuration.scripts.product_filter.enabled ?? true,
            script: configuration.scripts.product_filter.script ?? '',
          };
        }
        if (configuration.scripts.product_disambiguation) {
          scriptsPayload.product_disambiguation = {
            type: configuration.scripts.product_disambiguation.type || 'CEL',
            enabled: configuration.scripts.product_disambiguation.enabled ?? true,
            script: configuration.scripts.product_disambiguation.script ?? '',
          };
        }
        // Always send this key (as null when unset) — NOT conditionally. The backend
        // deep-merges the payload and preserves keys that are OMITTED, so omitting it
        // on a reset-to-default would silently keep the previously-stored custom order.
        // Sending null overwrites it, and the backend treats null as "use default order".
        scriptsPayload.customer_search_strategy_order =
          configuration.scripts.customer_search_strategy_order ?? null;
        if (configuration.scripts.quote_matching) {
          scriptsPayload.quote_matching = configuration.scripts.quote_matching;
        }
      }

      const response: AxiosResponse<TenantConfigurationResponse> = await apiClient.post(`/config/tenant/${tenantId}`, {
        // NOT sent: `portal` and `license` (owned by the Portal settings tab via
        // partial updates — replaying this snapshot would undo a toggle made
        // there) and the deprecated `features` block.
        config_data: {
          identity: configuration.identity,
          email_folders: configuration.email_folders,
          email_agent_connector_type: configuration.email_agent_connector_type,
          ai_models: configuration.ai_models,
          embedding_models: configuration.embedding_models,
          product_search: configuration.product_search,
          pdf_processing: configuration.pdf_processing,
          email_processing: configuration.email_processing,
          connectors: filteredConnectors,
          document_integrations: filteredDocumentIntegrations,
          notifications: configuration.notifications,
          twilio_voice_agent: configuration.twilio_voice_agent,
          scripts: scriptsPayload,
          replenishment: configuration.replenishment,
          leadgen: configuration.leadgen,
          schedulers: configuration.schedulers,
          // Prune empty data_ingestion sub-sections so the UI can always show
          // all three accordions (quote_catalog, customer_catalog, customer_role_links)
          // without persisting blank config to the backend.
          data_ingestion: (() => {
            if (!configuration.data_ingestion) return undefined;
            const hasMeaningfulIngestionData = (obj: Record<string, any>): boolean => {
              return Object.entries(obj).some(([k, v]) => {
                if (k === 'integration_type') return false;
                if (v === undefined || v === null || v === '') return false;
                if (typeof v === 'object' && !Array.isArray(v)) {
                  return Object.entries(v).some(([nk, nv]) => nk !== 'integration_type' && nv !== undefined && nv !== null && nv !== '');
                }
                if (Array.isArray(v)) return v.length > 0;
                return true;
              });
            };
            const pruned: Record<string, any> = {};
            for (const [key, section] of Object.entries(configuration.data_ingestion)) {
              if (section && typeof section === 'object' && hasMeaningfulIngestionData(section as Record<string, any>)) {
                pruned[key] = section;
              }
            }
            return Object.keys(pruned).length > 0 ? pruned : undefined;
          })(),
          document_rules: configuration.document_rules,
          prompt_extensions: configuration.prompt_extensions,
          hitl: configuration.hitl,
          plugins: configuration.plugins,
          // Emit only the new key. State is normalized on load, so this is a
          // defensive coalesce; returns undefined (and is dropped) when neither
          // key exists, so untouched tenants never get an empty block.
          work_description_mapping: coalesceWorkDescriptionMapping(configuration),
        },
        ...(options.baseHash ? { base_hash: options.baseHash } : {}),
      });
      
      console.log('✅ [TENANT] Tenant configuration update response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });
      
      // The POST endpoint might return a different structure than GET
      // Since we got a 200 response, consider it successful regardless of structure
      return {
        message: 'Configuration updated successfully',
        tenant_id: tenantId,
        config: configuration,
        status: 'updated',
        config_hash: response.data?.config_hash,
      };
    } catch (error: any) {
      console.error('❌ [TENANT] Error updating tenant configuration:', {
        tenantId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      // Forward FastAPI 422 validation errors with structured detail so callers
      // can surface them inline (e.g. next to the offending row).
      if (error.response?.status === 422 && Array.isArray(error.response.data?.detail)) {
        const detail = error.response.data.detail;
        const validationError: any = new Error(detail[0]?.msg || 'Validation failed');
        validationError.status = 422;
        validationError.detail = detail;
        throw validationError;
      }
      const saveError = toConfigSaveError(error);
      if (saveError) throw saveError;
      // Preserve the original error message if it's a validation error we threw
      // Otherwise use a generic error message
      if (error.message && (
        error.message.includes('connector is incomplete') ||
        error.message.includes('Missing required fields')
      )) {
        throw error; // Re-throw the original error with the specific message
      }
      throw new Error(error.message || 'Failed to update tenant configuration');
    }
  }

  static async activateTenant(tenantId: string): Promise<any> {
    try {
      console.log('🟢 [TENANT] Activating tenant:', {
        tenantId,
        url: `${API_BASE_URL}/infrastructure/scheduler/activate-tenant/${tenantId}`,
        method: 'POST'
      });
      
      const response: AxiosResponse<any> = await apiClient.post(`/infrastructure/scheduler/activate-tenant/${tenantId}`);
      
      console.log('✅ [TENANT] Activate tenant response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [TENANT] Error activating tenant:', {
        tenantId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to activate tenant');
    }
  }

  static async deactivateTenant(tenantId: string): Promise<any> {
    try {
      console.log('🔴 [TENANT] Deactivating tenant:', {
        tenantId,
        url: `${API_BASE_URL}/infrastructure/scheduler/deactivate-tenant/${tenantId}`,
        method: 'POST'
      });
      
      const response: AxiosResponse<any> = await apiClient.post(`/infrastructure/scheduler/deactivate-tenant/${tenantId}`);
      
      console.log('✅ [TENANT] Deactivate tenant response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [TENANT] Error deactivating tenant:', {
        tenantId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to deactivate tenant');
    }
  }

  static async getVoices(): Promise<VoiceDatabaseResponse> {
    try {
      console.log('🎤 [TENANT] Fetching voice database:', {
        url: `${API_BASE_URL}/config/tenant/voices`,
        method: 'GET'
      });
      
      const response: AxiosResponse<VoiceDatabaseResponse> = await apiClient.get('/config/tenant/voices');
      
      console.log('✅ [TENANT] Voice database response:', {
        status: response.status,
        statusText: response.statusText,
        count: response.data.voices.length
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [TENANT] Error fetching voice database:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to fetch voice database');
    }
  }

  static async deleteTenant(tenantId: string): Promise<void> {
    try {
      console.log('🗑️ [TENANT] Deleting tenant:', {
        tenantId,
        url: `${API_BASE_URL}/config/tenant/${tenantId}`,
        method: 'DELETE'
      });
      
      const response: AxiosResponse<any> = await apiClient.delete(`/config/tenant/${tenantId}`);
      
      console.log('✅ [TENANT] Delete tenant response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });
    } catch (error: any) {
      console.error('❌ [TENANT] Error deleting tenant:', {
        tenantId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to delete tenant');
    }
  }

  static async downloadTenantConfig(tenantId: string): Promise<{ blob: Blob; filename: string }> {
    try {
      console.log('📥 [TENANT] Downloading tenant configuration:', {
        tenantId,
        url: `${API_BASE_URL}/config/tenant/${tenantId}/export`,
        method: 'GET'
      });
      
      const response: AxiosResponse<Blob> = await apiClient.get(`/config/tenant/${tenantId}/export`, {
        responseType: 'blob'
      });
      
      // Extract filename from content-disposition header if available
      let filename = `tenant_config_${tenantId}.yaml`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      
      console.log('✅ [TENANT] Tenant configuration download response:', {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers['content-type'],
        contentLength: response.headers['content-length'],
        contentDisposition: response.headers['content-disposition'],
        filename
      });
      
      return { blob: response.data, filename };
    } catch (error: any) {
      console.error('❌ [TENANT] Error downloading tenant configuration:', {
        tenantId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      throw new Error('Failed to download tenant configuration');
    }
  }

  static async testErpConnector(tenantId: string, connectorType: string): Promise<any> {
    try {
      console.log('🧪 [TENANT] Testing ERP connector:', {
        tenantId,
        connectorType,
        url: `${API_BASE_URL}/config/connectors/test/erp/${tenantId}?connector_type=${connectorType}`,
        method: 'GET'
      });
      
      const response: AxiosResponse<any> = await apiClient.get(`/config/connectors/test/erp/${tenantId}`, {
        params: {
          connector_type: connectorType
        }
      });
      
      console.log('✅ [TENANT] ERP connector test response:', {
        status: response.status,
        statusText: response.statusText,
        success: response.data.success
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [TENANT] Error testing ERP connector:', {
        tenantId,
        connectorType,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      // Return error response if available, otherwise throw
      if (error.response?.data) {
        return error.response.data;
      }
      throw new Error(error.message || 'Failed to test ERP connector');
    }
  }

  /**
   * Test SAP IDoc transports (HTTP / Email).
   * Uses connector_type = 'idoc_http' | 'idoc_email'.
   */
  static async testSapConnector(tenantId: string, connectorType: string): Promise<any> {
    try {
      console.log('🧪 [TENANT] Testing SAP connector:', {
        tenantId,
        connectorType,
        url: `${API_BASE_URL}/config/connectors/test/sap/${tenantId}?connector_type=${connectorType}`,
        method: 'GET'
      });

      const response: AxiosResponse<any> = await apiClient.get(
        `/config/connectors/test/sap/${tenantId}`,
        {
          params: {
            connector_type: connectorType,
          },
        }
      );

      console.log('✅ [TENANT] SAP connector test response:', {
        status: response.status,
        statusText: response.statusText,
        success: response.data.success,
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ [TENANT] Error testing SAP connector:', {
        tenantId,
        connectorType,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data,
      });
      if (error.response?.data) {
        return error.response.data;
      }
      throw new Error(error.message || 'Failed to test SAP connector');
    }
  }

  static async testEmailConnector(tenantId: string, connectorType: string): Promise<any> {
    try {
      console.log('🧪 [TENANT] Testing Email connector:', {
        tenantId,
        connectorType,
        url: `${API_BASE_URL}/config/connectors/test/email/${tenantId}?connector_type=${connectorType}`,
        method: 'GET'
      });
      
      const response: AxiosResponse<any> = await apiClient.get(`/config/connectors/test/email/${tenantId}`, {
        params: {
          connector_type: connectorType
        }
      });
      
      console.log('✅ [TENANT] Email connector test response:', {
        status: response.status,
        statusText: response.statusText,
        success: response.data.success
      });
      
      return response.data;
    } catch (error: any) {
      console.error('❌ [TENANT] Error testing Email connector:', {
        tenantId,
        connectorType,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data
      });
      // Return error response if available, otherwise throw
      if (error.response?.data) {
        return error.response.data;
      }
      throw new Error(error.message || 'Failed to test Email connector');
    }
  }

  static async getAvailablePlugins(tenantId?: string): Promise<AvailablePlugin[]> {
    try {
      const params = tenantId ? { tenant_id: tenantId } : {};
      const response = await apiClient.get('/plugins/available', { params });
      return response.data;
    } catch (error: any) {
      console.warn('⚠️ [TENANT] Failed to fetch available plugins:', error.message);
      return [];
    }
  }

  // ---------------------------------------------------------------------------
  // Config Sync — file-based, server-side diff/apply against the TARGET tenant.
  // Endpoints confirmed-pending against the backend (see ConfigSync.ts header /
  // work_with_claude/admin/tenant_config_sync_ui_plan.md Appendix).
  // ---------------------------------------------------------------------------

  /**
   * Preview a field-level diff between an uploaded source config and the target
   * tenant. Returns the changes plus a `base_hash` that must be threaded
   * unchanged into {@link syncApply}.
   */
  static async syncPreview(
    targetTenantId: string,
    body: SyncPreviewRequest,
  ): Promise<SyncPreviewResponse> {
    const url = `/config/tenant/${targetTenantId}/sync/preview`;
    try {
      console.log('🔄 [TENANT] Previewing config sync:', {
        targetTenantId,
        url: `${API_BASE_URL}${url}`,
        method: 'POST',
        source_tenant_id: body.source_tenant_id,
      });

      const response: AxiosResponse<SyncPreviewResponse> = await apiClient.post(url, body);

      console.log('✅ [TENANT] Config sync preview response:', {
        status: response.status,
        base_hash: response.data.base_hash,
        changes: response.data.changes?.length,
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ [TENANT] Error previewing config sync:', {
        targetTenantId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data,
      });
      throw extractApiError(error, 'Failed to preview config sync');
    }
  }

  /**
   * Apply the selected changes to the target tenant. On a `409` (the target
   * changed since preview) this resolves to a {@link SyncConflict} carrying the
   * fresh `base_hash` so the caller can silently re-preview and re-review —
   * rather than throwing. All other failures throw.
   */
  static async syncApply(
    targetTenantId: string,
    body: SyncApplyRequest,
  ): Promise<SyncApplyResponse | SyncConflict> {
    const url = `/config/tenant/${targetTenantId}/sync/apply`;
    try {
      console.log('🔄 [TENANT] Applying config sync:', {
        targetTenantId,
        url: `${API_BASE_URL}${url}`,
        method: 'POST',
        selected: body.selected_paths?.length,
        kept: body.keep_target_paths?.length,
      });

      const response: AxiosResponse<SyncApplyResponse> = await apiClient.post(url, body);

      console.log('✅ [TENANT] Config sync apply response:', {
        status: response.status,
        applied: response.data.applied_paths?.length,
        skipped: response.data.skipped_paths?.length,
        config_valid: response.data.config_valid,
      });

      return response.data;
    } catch (error: any) {
      // 409 → the target moved on. Surface the fresh base_hash so the caller can
      // re-preview transparently instead of treating it as a hard failure.
      if (error.response?.status === 409) {
        const freshHash = error.response.data?.base_hash;
        console.warn('⚠️ [TENANT] Config sync conflict (409) — target changed since preview:', {
          targetTenantId,
          base_hash: freshHash,
        });
        if (freshHash) {
          return { conflict: true, base_hash: freshHash };
        }
      }

      console.error('❌ [TENANT] Error applying config sync:', {
        targetTenantId,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data,
      });
      throw extractApiError(error, 'Failed to apply config sync');
    }
  }
}